'use client';

import type { ComponentProps, ReactNode } from 'react';

import { Calendar as CalendarPrimitive } from '../ui/calendar';
import { type DayCellState, dayCellStyles } from './calendar-styles';

export type CalendarProps = ComponentProps<typeof CalendarPrimitive>;

/**
 * Month grid (Figma: Calendar ↳ Date Picker, without the panel). The shadcn
 * Calendar on react-day-picker, restyled to Figma: Monday first, Day Cells
 * for today / selected / range / outside-month / disabled days. Keyboard:
 * arrows move, Home / End jump to the week edges, PageUp / PageDown change
 * month (Shift for year), Enter or Space selects. Takes every
 * react-day-picker prop (`mode`, `selected`, `onSelect`, `disabled`, …).
 * For a picker in a panel or popover use DatePickerPanel / DatePicker.
 */
export function Calendar(props: CalendarProps) {
  return <CalendarPrimitive {...props} />;
}

export type DayCellProps = Omit<ComponentProps<'button'>, 'children'> & {
  /** Figma "State". Hover is the pointer hovering a `default` / `today` / `outside` / `in-range` cell. */
  state?: DayCellState;
  /** The day number. */
  children: ReactNode;
};

/**
 * One day (Figma: Calendar ↳ Day Cell, 62:33), drawn on its own: a 40×40
 * button around the 36px chip. Calendar renders these for every day; use
 * DayCell directly only for custom grids (week strips, legends). A disabled
 * cell is `disabled`, struck through, and should be named "… unavailable".
 */
export function DayCell({
  state = 'default',
  className,
  children,
  ...props
}: DayCellProps) {
  const styles = dayCellStyles({ state });
  return (
    <button
      type="button"
      data-slot="day-cell"
      data-cell-state={state}
      disabled={state === 'disabled' || undefined}
      className={styles.root({ className })}
      {...props}
    >
      <span aria-hidden className={styles.band()} />
      <span
        data-slot="day-cell-chip"
        data-disabled={state === 'disabled' || undefined}
        className={styles.chip()}
      >
        {children}
      </span>
    </button>
  );
}

export type { DayCellState };
